import { AfterViewInit, ChangeDetectionStrategy, ChangeDetectorRef, Component, OnInit, } from '@angular/core';
import { select, Store } from '@ngrx/store';
import { AppState } from '@root/app-state.module';
import {
  selectAnnotationCount,
  selectCheckedPropertyIds,
  selectCheckedSearchResult,
  selectCurrentPageName,
  selectDrawingLines,
  selectDrawingShapes,
  selectDrawingTools,
  selectGridPage,
  selectIsGridCompactMode,
  selectMySearchFieldsData,
  selectResultsView,
  selectSearchResult,
  selectSearchResultsCount,
  selectSelectedGridRecords,
  selectSelectedGridView,
  selectSelectedPointsOfInterestProperty,
  selectSelectedRowId,
  selectShowSelected,
  selectSidePanelState,
  selectSidePanelTab,
  selectSortedPaginatedSearchResult,
  selectTotalGridRecords
} from '@store/search-board/search-board.selector';
import {
  ChangeAnnotationToolStatus,
  ChangeDrawingTool,
  ChangeGridData,
  ChangeResultsView,
  ChangeSidePanelTabView,
  ChangeSidePanelView,
  ClearSearchResults,
  ClearSelectedLayers,
  OpenActionBarModal,
  OpenSortModal,
  SaveCompactMode,
  SelectAllProperty,
  SelectAllPropertyMobile,
  SetGridPage,
  SetLocateOnMapTool,
  SetRecentSearches,
  SetResultsViewToCards,
  SetShowSelected,
  TogglePropertySelection
} from '@store/search-board/search-board.actions';
import {
  ActionModalTypes,
  GridDragEnd,
  GridItemSize,
  GridViewState,
  PropertyData,
  PropertyIdentifier,
  PropertyInformation,
  SelectedRow,
  Shape,
  SideNavMode,
  SideNavPosition,
  SidePanelTab,
  SidePanelView
} from '@store/search-board/search-board.model';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { combineLatest, Observable } from 'rxjs';
import { filter, map, pairwise, startWith, switchMap, take, tap, withLatestFrom } from 'rxjs/operators';
import {
  DataElement,
  MAX_NUMBER_OF_SAVED_PROPERTIES,
  PreferenceCode,
  PreferenceGroup,
  UserInfo
} from '@store/user/user.model';
import {
  selectCardsAttributes,
  selectCurrentCounties,
  selectCurrentCountiesTooltip,
  selectGoogleMapKey,
  selectGridHeaders,
  selectIsLoginInProgress,
  selectIsMobile,
  selectMaxEmailDownloadPreferences,
  selectPreferenceGroupByCode,
  selectUserInfo
} from '@store/user/user.selector';
import { OverlayType } from '@shared/models/googlemaps.model';
import { RIBBON_TYPE } from '@shared/models/ribbon.model';
import { RibbonService } from '@shared/services/ribbon.service';
import { OpenCardsCustomizeAttributes, OpenGridCustomizeHeaders } from '@store/user/user.actions';
import { PropertyUtil } from '@shared/utils/property-util';
import { selectSavedProperties } from '@store/saved-properties/saved-properties.selector';
import { UpdateSavedProperties } from '@store/saved-properties/saved-properties.actions';
import { NavigationService } from '@root/search/services/navigation.service';
import { selectAllSelectedLayers, selectNumberOfAllSelectedLayers } from '@store/map-layers.selector';
import { LayerInfo } from '@root/search/models/map.models';
import { PointOfInterestProperty } from '@root/search/models/map.constants';
import { MixpanelService } from '@shared/services/mixpanel.service';
import { SearchMixpanelUtil } from '@root/search/search-mixpanel-util';

import { areWeOnProduction } from '../../../../assets/js/util';
import { foresee } from '../../../../assets/js/vendor/foresee';
import { Router } from '@angular/router';



@UntilDestroy()
@Component({
  selector: 'rlst-search',
  templateUrl: './search.component.html',
  styleUrls: ['./search.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class SearchComponent implements OnInit, AfterViewInit {
  public sidePanelOpened: boolean;
  public selectedResultsTableView$: Observable<boolean>;
  public selectedRecords$: Observable<number>;
  public totalGridRecords$: Observable<number>;
  public sidePanelTabOpened: SidePanelTab;
  public rowData$: Observable<Array<PropertyData>>;
  public paginatedRowData$: Observable<Array<PropertyInformation>>;
  public sideNavPosition: SideNavPosition = SideNavPosition.slideToLeft;
  public sideNavMode: SideNavMode = SideNavMode.sideContent;
  public key$: Observable<string>;
  public isMobile: boolean = false;
  public isMobile$: Observable<boolean>;
  public gridState: GridViewState;
  public showSelectAllButton$: Observable<boolean>;
  public disableSelectAllButton$: Observable<boolean>;
  public rowId$: Observable<SelectedRow>;
  public checkedProperties$: Observable<Array<PropertyInformation>>;
  public checkedPropertyIdList$: Observable<Array<string>>;
  public savedProperties: Array<PropertyInformation>;
  public savedPropertyIdList: Array<string>;
  public gridHeaders$: Observable<Array<DataElement>>;
  public customCardsAttributes$: Observable<Array<DataElement>>;
  public indicatorFlagsPreference$: Observable<PreferenceGroup>;
  public userInfo$: Observable<UserInfo>;
  public selectedDrawingTool: OverlayType | null;
  public selectedCounty$: Observable<string>;
  public selectedCountyTooltip$: Observable<string>;
  public currentGridPage$: Observable<number>;
  public searchResultsCount$: Observable<number>;
  public currentPageName$: Observable<string>;
  public maxEmailDownloadPreferences$: Observable<number>;
  public isLoginInProgress$: Observable<boolean>;
  public showSelected$: Observable<boolean>;
  public isShowSelectedDisabled$: Observable<boolean>;
  public isEmailDisabled: boolean = false;
  public readonly drawingTools: typeof OverlayType = OverlayType;
  public readonly gridItemSize: typeof GridItemSize = GridItemSize;
  public mapIsLoaded: boolean = false;
  public isCompactMode: boolean = false;
  public hasClearAll$: Observable<boolean>;
  public getIdFromPropertyInformation: Function = PropertyUtil.getIdFromPropertyInformation;
  public isMySearch: boolean;
  private shapes: Array<Shape> = [];
  private readonly shapesLimit: number = 5;
  private readonly annotationLimit: number = 5;

  private readonly searchMixpanelUtil: SearchMixpanelUtil = new SearchMixpanelUtil(this.store$);


  constructor(private readonly store$: Store<AppState>,
              private readonly cdr: ChangeDetectorRef,
              private readonly ribbonService: RibbonService,
              private readonly navigationService: NavigationService,
              private readonly mixpanelService: MixpanelService,
              private readonly router: Router
  ) {
    const state = this.router.getCurrentNavigation()?.extras?.state;
    if(state && state.isDashboard){
      this.openTab(SidePanelTab.mySearch);
      this.store$.dispatch(new ChangeSidePanelView(true));
    }
  }

  ngOnInit(): void {
    this.getFromStore();
    this.getBreakpoint();
    this.showSelectAllButton$ = this.isMobile$.pipe(
      switchMap((isMobile: boolean) => isMobile ?
        combineLatest(
          [
            this.checkedPropertyIdList$,
            this.paginatedRowData$
          ]
        ).pipe(
          map(([checkedProperties, rowData]) => (checkedProperties.length === rowData.length) && rowData.length !== 0)
        ) :
        combineLatest(
          [
            this.totalGridRecords$,
            this.selectedRecords$
          ]
        ).pipe(
          map(([totalGridRecords, selectedRecords]) => (totalGridRecords === selectedRecords) && totalGridRecords !== 0)
        )
      )
    );
    this.disableSelectAllButton$ = this.totalGridRecords$.pipe(
      map((totalGridRecords) => totalGridRecords === 0)
    );

    const recentSearches = window.localStorage.getItem('recentSearches');

    if (recentSearches) {
      this.store$.dispatch(new SetRecentSearches(JSON.parse(recentSearches)));
    }

    this.store$.select(selectUserInfo).subscribe((item) => {
      if (item.showSurveyLink) {
        areWeOnProduction() ? foresee('realist', 'production') : foresee('realist', 'staging');
      }
    });
  }

  ngAfterViewInit() {
    this.mixpanelService.track('Search - Landed');
  }

  public showSelectedProperties(): void {
    this.store$.dispatch(new SetShowSelected(true));
  }

  public showAllProperties(): void {
    this.store$.dispatch(new SetShowSelected(false));
  }

  private getFromStore(): void {
    this.key$ = this.store$.pipe(select(selectGoogleMapKey));
    this.selectedCounty$ = this.store$.pipe(select(selectCurrentCounties));
    this.selectedCountyTooltip$ = this.store$.pipe(select(selectCurrentCountiesTooltip));
    this.currentGridPage$ = this.store$.pipe(select(selectGridPage));
    this.maxEmailDownloadPreferences$ = this.store$.pipe(select(selectMaxEmailDownloadPreferences));

    this.store$
      .pipe(
        select(selectSidePanelState),
        untilDestroyed(this)
      )
      .subscribe((sidePanelView: SidePanelView) => {
        this.sidePanelOpened = sidePanelView.sidePanelOpened;
        this.sidePanelTabOpened = sidePanelView.sidePanelTabOpened;
        this.cdr.markForCheck();
      });

    this.store$
      .pipe(
        select(selectSelectedGridView),
        untilDestroyed(this)
      )
      .subscribe((state: GridViewState) => this.gridState = state);

    this.selectedResultsTableView$ = this.store$
      .pipe(select(selectResultsView));

    this.selectedRecords$ = this.store$
      .pipe(select(selectSelectedGridRecords));

    this.totalGridRecords$ = this.store$
      .pipe(select(selectTotalGridRecords));

    this.searchResultsCount$ = this.store$
      .pipe(
        select(selectSearchResultsCount),
        withLatestFrom(
          this.store$.pipe(select(selectMySearchFieldsData))
        ),
        map(([searchCount, formValue]) => {
          if (this.isMySearch && (!formValue || !formValue.length)) {
            return 0;
          }

          return searchCount;
        })
      );

    this.currentPageName$ = this.store$
      .pipe(select(selectCurrentPageName));

    this.userInfo$ = this.store$
      .pipe(select(selectUserInfo));

    this.gridHeaders$ = this.store$
      .pipe(select(selectGridHeaders));

    this.customCardsAttributes$ = this.store$
      .pipe(select(selectCardsAttributes));

    this.store$
      .pipe(
        select(selectDrawingShapes),
        untilDestroyed(this)
      )
      .subscribe((shapes: Array<Shape>) => this.shapes = shapes);

    this.rowData$ = this.store$
      .pipe(
        select(selectSearchResult),
        map((data: Array<PropertyInformation>) => data.map((el: PropertyInformation) => el.propertyData))
      );

    this.paginatedRowData$ = this.store$
      .pipe(
        select(selectSortedPaginatedSearchResult)
      );

    this.rowId$ = this.store$
      .pipe(select(selectSelectedRowId));

    this.store$
      .pipe(
        select(selectDrawingTools),
        untilDestroyed(this)
      )
      .subscribe((tool: OverlayType | null) => {
        this.selectedDrawingTool = tool;
        this.cdr.markForCheck();
      });

    this.checkedProperties$ = this.store$.pipe(select(selectCheckedSearchResult));
    this.checkedPropertyIdList$ = this.store$.pipe(select(selectCheckedPropertyIds));
    this.isLoginInProgress$ = this.store$.pipe(select(selectIsLoginInProgress));
    this.showSelected$ = this.store$.pipe(select(selectShowSelected));

    this.isShowSelectedDisabled$ = combineLatest([
      this.showSelected$,
      this.selectedRecords$
    ])
      .pipe(
        map(([showSelected, selectedRecords]: [boolean, number]) => {
          return showSelected || selectedRecords === 0;
        })
      );

    this.indicatorFlagsPreference$ = this.store$.pipe(
      select(selectPreferenceGroupByCode(PreferenceCode.PG_HIDE_FORECLOSURE_FLAGS))
    );

    this.hasClearAll$ = combineLatest([
      this.store$.pipe(select(selectNumberOfAllSelectedLayers)),
      this.store$.pipe(select(selectAnnotationCount)),
      combineLatest([
        this.store$.pipe(select(selectDrawingShapes)),
        this.store$.pipe(select(selectSidePanelTab)),
      ]).pipe(
        map(([shapes, sidePanelTab]: [Array<Shape>, SidePanelTab]) => {
          return sidePanelTab === SidePanelTab.mySearch ? shapes.length : 0;
        })
      ),
      this.store$.pipe(
        select(selectDrawingLines),
        map((lines: Array<Shape>) => lines.length)
      ),
      this.store$.pipe(
        select(selectSelectedPointsOfInterestProperty),
        map((points: Array<PointOfInterestProperty>) => points.length)
      )
    ])
      .pipe(
        map((selections: Array<number>) => {
          return selections.some((numberOfSelected) => numberOfSelected > 0);
        })
      );

    this.store$.pipe(
      select(selectSavedProperties),
      untilDestroyed(this)
    ).subscribe((savedProperties: Array<PropertyInformation>) => {
      this.savedProperties = savedProperties;
      this.savedPropertyIdList = savedProperties
        .map((property: PropertyInformation) => PropertyUtil.getCombinedIdFromPropertyInformation(property));
      this.cdr.markForCheck();
    });

    this.store$.pipe(
      select(selectIsGridCompactMode),
      take(1)
    )
      .subscribe((isCompactMode: boolean) => {
        this.isCompactMode = isCompactMode;
        this.cdr.markForCheck();
      });
  }

  public toggleSidePanel(): void {
    this.store$.dispatch(new ChangeSidePanelView(!this.sidePanelOpened));
  }

  public toggleCompactMode(): void {
    this.isCompactMode = !this.isCompactMode;
    this.store$.dispatch(new SaveCompactMode(this.isCompactMode));
  }

  public changeGridView(m: number, l: number): void {
    if (m === this.gridItemSize.ZERO && l === this.gridItemSize.FULL && this.isMobile) {
      this.store$.dispatch(new SetResultsViewToCards());
    }
    this.store$.dispatch(new ChangeGridData({map: m, list: l}));
    this.searchMixpanelUtil.setupMixPanelItems(this.sidePanelTabOpened);

    this.mixpanelService.track('Search - Result Display Changed', {
      countyList: this.searchMixpanelUtil.countyList,
      searchType: this.sidePanelTabOpened,
      isSavedSearch: (this.sidePanelTabOpened === 'quickSearch') ? false :
        this.searchMixpanelUtil.selectedSearch !== 'My Search Default',
      searchParameters: this.searchMixpanelUtil.searchFieldNames.join('; '),
      numberOfResults: this.searchMixpanelUtil.totalSearchRecords,
      resultDisplay: (m === this.gridItemSize.ZERO)
        ? 'List'
        : m === this.gridItemSize.HALF
          ? 'Split'
          : 'Map'
    });

  }

  public changeGridPage(pageNumber: number): void {
    this.searchMixpanelUtil.setupMixPanelItems(this.sidePanelTabOpened);
    const pageRange: string = (pageNumber === 0)
      ? '1 - 1000'
      : (pageNumber === 1)
        ? '1001 - 2000'
        : '2001 - 3000';

    this.mixpanelService.track('Search - Result Pagination', {
      searchType: this.sidePanelTabOpened,
      isSavedSearch: (this.sidePanelTabOpened === 'quickSearch') ? false :
        this.searchMixpanelUtil.selectedSearch !== 'My Search Default',
      searchParameters: this.searchMixpanelUtil.searchFieldNames.join('; '),
      countyList: this.searchMixpanelUtil.countyList,
      numberOfResults: this.searchMixpanelUtil.totalSearchRecords,
      gridResultsDisplayed: pageRange
    });

    this.store$.dispatch(new SetGridPage(pageNumber));
  }

  public openTab(tab: SidePanelTab): void {
    this.isMySearch = tab === SidePanelTab.mySearch ? true : false;
    this.store$.dispatch(new ChangeSidePanelTabView(tab));
  }

  public changeResultsView(): void {
    this.store$.dispatch(new ChangeResultsView());
  }

  public gridDragEnd(e: GridDragEnd): void {
    this.changeGridView(e.sizes[0], e.sizes[1]);
  }

  public onSelectAll({target}: { target: HTMLInputElement }): void {
    if (this.isMobile) {
      combineLatest(
        [
          this.checkedPropertyIdList$,
          this.paginatedRowData$
        ]
      ).pipe(
        take(1)
      ).subscribe(([checkedProperties, data]: [Array<string>, Array<PropertyInformation>]) => {
        let selectedProperties: Array<string> = [];
        if (checkedProperties.length !== data.length) {
          selectedProperties = data.map((property: PropertyInformation) => PropertyUtil.getIdFromPropertyInformation(property));
        }
        this.store$.dispatch(new SelectAllPropertyMobile(selectedProperties));
      });
    } else {
      this.store$.dispatch(new SelectAllProperty(target.checked));
    }
  }

  public openCustomizeCardModal(): void {
    this.store$.dispatch(new OpenCardsCustomizeAttributes());
    this.logOpenCustomizeModalMixPanel('Search - Customize List Display Option - Cards');
  }

  public openCustomizeTableModal(): void {
    this.store$.dispatch(new OpenGridCustomizeHeaders());
    this.logOpenCustomizeModalMixPanel('Search - Customize List Display Option - Grid');
  }

  public openSortModal(): void {
    this.store$.dispatch(new OpenSortModal());
  }

  public openEmailModal(): void {
    this.checkedPropertyIdList$.pipe(
      take(1),
      map((checkedProperties) => checkedProperties.length),
      tap((numberOfCheckedProperties: number) => {
        if (!numberOfCheckedProperties) {
          this.ribbonService.openRibbon(RIBBON_TYPE.WARNING, 'Please select properties to Email.');
        }
      }),
      filter(Boolean),
      switchMap(() => this.checkedProperties$.pipe(
        take(1)
      )),
      map((checkedProperties: Array<PropertyInformation>) => PropertyUtil.getPropertyIdentifiers(checkedProperties))
    )
      .subscribe((propertyIdentifiers: Array<PropertyIdentifier>) => {
        const payload = {
          modalType: ActionModalTypes.EMAIL,
          propertyIdentifiers,
          reportsWithMapDisabled: this.gridState.list === this.gridItemSize.FULL
        };
        return this.store$.dispatch(new OpenActionBarModal(payload));
      });
  }

  public clearSearchResults(): void {
    this.store$.dispatch(new ClearSearchResults());
    this.store$.dispatch(new SetShowSelected(false));
  }

  public useDrawingTool(toolName: OverlayType): void {
    if (this.shapes.length < this.shapesLimit || toolName === OverlayType.POLYLINE) {
      this.openTab(SidePanelTab.mySearch);
      this.store$.dispatch(new ChangeDrawingTool(toolName));
    } else {
      this.ribbonService.openRibbon(RIBBON_TYPE.WARNING, `You can draw only ${this.shapesLimit} shapes.`);
    }
  }

  public useAnnotationTool(): void {
    this.store$.pipe(
      select(selectAnnotationCount),
      take(1)
    ).subscribe((count: number) => {
      if (count < this.annotationLimit) {
        this.store$.dispatch(new ChangeAnnotationToolStatus());
      } else {
        this.ribbonService.openRibbon(RIBBON_TYPE.WARNING, `You can add only ${this.annotationLimit} annotation.`);
      }
    });
  }

  public clearSelectedLayers(): void {
    this.store$.pipe(
      select(selectAllSelectedLayers),
      take(1)
    ).subscribe((selectedLayers: Array<LayerInfo>) => {
      this.store$.dispatch(new ClearSelectedLayers(selectedLayers));
    });

    this.logMapBoundariesMixPanel('Search - Clear All Selected Layers', 'All selected');
  }

  public locateOnMapToolEvent(open: boolean): void {
    this.store$.dispatch(new SetLocateOnMapTool(open));
  }

  public toggleChecked([property, checked]: [PropertyInformation, boolean]): void {
    this.store$.dispatch(new TogglePropertySelection(
      {propertyId: PropertyUtil.getIdFromPropertyInformation(property), isChecked: checked}));
  }

  public toggleSaved([property, saved]: [PropertyInformation, boolean]): void {
    if (saved) {
      const updatedSavedProperties = this.savedProperties.filter((propertyInformation: PropertyInformation) =>
        PropertyUtil.getCombinedIdFromPropertyInformation(propertyInformation)
        !== PropertyUtil.getCombinedIdFromPropertyInformation(property));
      this.mixPanelServiceTracking('Property Unstarred', property);
      this.store$.dispatch(new UpdateSavedProperties(updatedSavedProperties));
    } else {
      if (this.savedProperties.length < MAX_NUMBER_OF_SAVED_PROPERTIES) {
        const updatedSavedProperties = [property, ...this.savedProperties];
        this.mixPanelServiceTracking('Property Starred', property);
        this.store$.dispatch(new UpdateSavedProperties(updatedSavedProperties));
        this.ribbonService.openRibbon(RIBBON_TYPE.SUCCESS, 'The property has been added to saved property page.');
      } else {
        this.ribbonService.openRibbon(RIBBON_TYPE.WARNING, 'Sorry, you can only select up to 50 properties. ' +
          'Please de-select other saved  properties and try again.');
      }
    }
  }

  public startToSearch(): void {
    this.store$.dispatch(new ChangeSidePanelTabView(SidePanelTab.quickSearch));
    this.store$.dispatch(new ChangeSidePanelView(true));
  }

  public openForeclosureReport(): void {
    combineLatest(
      [
        this.store$.pipe(select(selectSearchResult)),
        this.rowId$
      ]
    ).pipe(
      take(1),
      map(([properties, rowId]: [Array<PropertyInformation>, SelectedRow]) =>
        properties.find((property: PropertyInformation) => PropertyUtil.getIdFromPropertyInformation(property) === rowId.current)
      ),
      filter(Boolean)
    ).subscribe((property: PropertyInformation) => {
      this.navigationService.openForeclosureReport(property);
    });
  }

  public openPropertyDetailsReport(propertyId: string): void {
    this.store$.pipe(
      select(selectSearchResult),
      map((properties: Array<PropertyInformation>) =>
        properties.find((property: PropertyInformation) => PropertyUtil.getIdFromPropertyInformation(property) === propertyId)
      ),
      take(1)
    )
      .subscribe((propertyInformation: PropertyInformation) => {
        this.navigationService.openSingleReport(propertyInformation);
      });
  }

  public setMapIsLoaded(): void {
    this.mapIsLoaded = true;
  }

  private getBreakpoint(): void {
    this.isMobile$ = this.store$.pipe(select(selectIsMobile));
    this.isMobile$.pipe(untilDestroyed(this))
      .subscribe((breakpoint: boolean) => {
        this.isMobile = breakpoint;

        if (breakpoint) {
          if (this.gridState.map !== this.gridItemSize.FULL && this.gridState.list !== this.gridItemSize.FULL) {
            this.changeGridView(this.gridItemSize.FULL, this.gridItemSize.ZERO);
          }
          this.store$.dispatch(new SelectAllPropertyMobile([]));
          this.sideNavPosition = SideNavPosition.slideToRight;
          this.sideNavMode = SideNavMode.overlapContent;
        } else {
          this.sideNavPosition = SideNavPosition.slideToLeft;
          this.sideNavMode = SideNavMode.sideContent;
        }
        this.cdr.markForCheck();
      });

    combineLatest(
      [
        this.selectedResultsTableView$.pipe(filter((isTableView) => !isTableView)),
        this.isMobile$.pipe(filter(Boolean))
      ]
    )
      .pipe(
        switchMap(() => this.maxEmailDownloadPreferences$),
        switchMap((maxEmailDownloadPreferences: number) => {
          return this.checkedPropertyIdList$.pipe(
            startWith([]),
            pairwise(),
            tap(([prevValue, nextValue]: [Array<string>, Array<string>]) => {
              if (prevValue.length <= maxEmailDownloadPreferences &&
                nextValue.length > maxEmailDownloadPreferences) {
                this.ribbonService.openRibbon(
                  RIBBON_TYPE.WARNING,
                  `Email option is not available if more than ${maxEmailDownloadPreferences} results are selected.`
                );
                this.isEmailDisabled = true;
              } else {
                this.isEmailDisabled = false;
              }
            })
          );
        }),
        untilDestroyed(this)
      )
      .subscribe();
  }

  private logMapBoundariesMixPanel(eventId: string, boundaryType: string) {
    this.searchMixpanelUtil.setupMixPanelItems(this.sidePanelTabOpened);

    this.mixpanelService.track(eventId, {
      countyList: this.searchMixpanelUtil.countyList,
      searchType: this.sidePanelTabOpened,
      isSavedSearch: (this.sidePanelTabOpened === 'quickSearch') ? false :
        this.searchMixpanelUtil.selectedSearch !== 'My Search Default',
      searchParameters: this.searchMixpanelUtil.searchFieldNames.join('; '),
      boundaryType: boundaryType
    });
  }

  private logOpenCustomizeModalMixPanel(eventId: string) {
    this.searchMixpanelUtil.setupMixPanelItems(this.sidePanelTabOpened);

    this.mixpanelService.track(eventId, {
      countyList: this.searchMixpanelUtil.countyList,
      searchType: this.sidePanelTabOpened,
      isSavedSearch: (this.sidePanelTabOpened === 'quickSearch') ? false :
        this.searchMixpanelUtil.selectedSearch !== 'My Search Default',
      searchParameters: this.searchMixpanelUtil.searchFieldNames.join('; '),
      numberOfResults: this.searchMixpanelUtil.totalSearchRecords
    });
  }

  private mixPanelServiceTracking(eventId: string, propertyInformation: PropertyInformation) {
    const propertyId = PropertyUtil.getCombinedIdFromPropertyInformation(propertyInformation);
    this.mixpanelService.track(eventId, {
      propertyIdentifier: propertyId,
      mlsStatus: propertyInformation.propertyData.MLS_STD_MLS_STATUS
    });
  }
}
